import { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useUser } from 'reactfire'
import {
  FeatureFlags,
  FeatureFlagsContext,
  resolveAudienceUid,
} from './FeatureFlagsContext'

export function useFeatureFlags(): FeatureFlags {
  return (
    useContext<FeatureFlags | null>(FeatureFlagsContext) ??
    new FeatureFlags(null)
  )
}

export function useFeatureFlag(key: string): boolean {
  const featureFlags = useFeatureFlags()
  const location = useLocation()
  const [enabled, setEnabled] = useState(() =>
    featureFlags.getResolvedBoolean(key)
  )

  useEffect(() => {
    let isMounted = true

    setEnabled(featureFlags.getResolvedBoolean(key))
    void featureFlags.ready.finally(() => {
      if (isMounted) {
        setEnabled(featureFlags.getResolvedBoolean(key))
      }
    })

    return () => {
      isMounted = false
    }
  }, [featureFlags, key, location.search])

  return enabled
}

export interface AudienceFlagState {
  loading: boolean
  allowed: boolean
}

/**
 * Reusable audience-gated feature flag hook. Resolves a Remote Config flag for
 * the current Firebase UID and models loading separately so a route can hold
 * before the flag value is known instead of redirecting on a stale default.
 */
export function useAudienceFlag(key: string): AudienceFlagState {
  const featureFlags = useFeatureFlags()
  const location = useLocation()
  const { data: user } = useUser()

  // Resolve the effective identity for the gate. While impersonating, the
  // Firebase currentUser is the impersonated user, so we evaluate against the
  // impersonator's original uid (from token claims) instead of currentUser.uid.
  const [uidState, setUidState] = useState<{
    uid: string | null
    loading: boolean
  }>({ uid: null, loading: true })

  useEffect(() => {
    // `user` is undefined while auth is still resolving; null once logged out.
    if (user === undefined) {
      setUidState({ uid: null, loading: true })
      return
    }
    if (user === null) {
      setUidState({ uid: null, loading: false })
      return
    }

    let cancelled = false
    user
      .getIdTokenResult()
      .then((result) => {
        if (cancelled) return
        setUidState({
          uid: resolveAudienceUid(result.claims, user.uid),
          loading: false,
        })
      })
      .catch(() => {
        if (cancelled) return
        setUidState({ uid: user.uid, loading: false })
      })

    return () => {
      cancelled = true
    }
  }, [user])

  const evaluate = useCallback(
    () => featureFlags.getAudienceFlag(key, uidState.uid),
    [featureFlags, key, uidState.uid]
  )

  const [state, setState] = useState<AudienceFlagState>(() => ({
    loading: true,
    allowed: false,
  }))

  useEffect(() => {
    let isMounted = true

    if (uidState.loading) {
      setState({ loading: true, allowed: false })
      return () => {
        isMounted = false
      }
    }

    if (featureFlags.isReady()) {
      setState({ loading: false, allowed: evaluate() })
      return () => {
        isMounted = false
      }
    }

    setState({ loading: true, allowed: false })
    void featureFlags.ready.finally(() => {
      if (isMounted) {
        setState({ loading: false, allowed: evaluate() })
      }
    })

    return () => {
      isMounted = false
    }
  }, [evaluate, featureFlags, location.search, uidState.loading])

  return state
}
